import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button';

export interface ModalProps {
  title?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
  showClose?: boolean;
}

// Generic centered dialog. ConfirmDialog builds on this for the
// confirm/cancel use case; use Modal directly for anything else that needs a
// centered box (as opposed to Drawer's right-side sliding panel).
//
// Portaled to document.body so it always covers the full viewport — a caller
// rendered inside a `transform`ed ancestor (e.g. a sliding drawer) would
// otherwise become this modal's containing block for its `fixed` overlay,
// confining it to that ancestor's box instead of the whole page.
export const Modal = ({ title, onClose, children, footer, maxWidth = 'max-w-sm', showClose = true }: ModalProps) =>
  createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${maxWidth} p-6`}>
        {(title || showClose) && (
          <div className="flex items-center justify-between gap-3">
            {title && <h3 className="text-[16px] font-semibold text-gray-900">{title}</h3>}
            {showClose && (
              <Button
                variant="tertiary"
                onClick={onClose}
                className="w-8 h-8 !p-0 ml-auto"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>
        )}
        <div className={title || showClose ? 'mt-2' : ''}>{children}</div>
        {footer && <div className="flex items-center justify-end gap-3 mt-6">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
